// Help dialog summarizing every keyboard shortcut. Bound to the `?` key.

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

const SECTIONS: Array<{ title: string; rows: Array<{ keys: string; label: string }> }> = [
  {
    title: "Global",
    rows: [
      { keys: "⌘ K", label: "Open command palette" },
      { keys: "/", label: "Open command palette" },
      { keys: "?", label: "Show this help" },
      { keys: "Esc", label: "Close any dialog" },
    ],
  },
  {
    title: "Navigation (chord — press g, then letter)",
    rows: [
      { keys: "g h", label: "Home / Today" },
      { keys: "g i", label: "Inbox" },
      { keys: "g c", label: "Contacts" },
      { keys: "g o", label: "Companies" },
      { keys: "g d", label: "Deals" },
      { keys: "g t", label: "Contracts" },
      { keys: "g k", label: "Tasks" },
      { keys: "g a", label: "Approvals" },
      { keys: "g r", label: "Reports" },
    ],
  },
  {
    title: "Inbox composer",
    rows: [
      { keys: "⌘ Enter", label: "Send reply" },
      { keys: "⌘ S", label: "Save as draft" },
    ],
  },
  {
    title: "Tables",
    rows: [
      { keys: "Click header", label: "Cycle sort: asc → desc → clear" },
      { keys: "Click row", label: "Open detail" },
    ],
  },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function HelpDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-brand" /> Keyboard shortcuts
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-dim">
                {s.title}
              </h3>
              <dl className="space-y-1.5">
                {s.rows.map((r) => (
                  <div key={r.label} className="flex items-center justify-between gap-3">
                    <dt className="text-sm text-ink">{r.label}</dt>
                    <dd>
                      <kbd className="rounded border border-line bg-surface-panel2 px-1.5 py-0.5 font-mono text-[10px] text-ink-muted">
                        {r.keys}
                      </kbd>
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-ink-dim">
          Tip: shortcuts are disabled while you're typing into a textarea or input.
        </p>
      </DialogContent>
    </Dialog>
  );
}
